/*
 * What this browser has already played, kept in localStorage.
 *
 * There are no accounts here and there is no reason to invent one. The daily
 * game is the same for everybody, so the only thing worth remembering is
 * whether *this* browser has had its go at today and what it scored — which is
 * a fact about the machine, not about a person. localStorage rather than a
 * cookie: nothing about it needs to reach the server, and a cookie would be sent
 * with every request for a star catalogue and a moon texture.
 *
 * Deliberately not a lock. Knowing you have played is useful; being stopped from
 * playing again is not something this game needs to enforce, and a browser-side
 * record could not enforce it anyway.
 */

import { dayKey } from './daily.mjs';

const STORAGE_KEY = 'starguessr:played';

// Enough to cover the corpus's fortnight without growing without bound.
const KEEP_DAYS = 30;

function read() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Private windows, blocked site data, or something else's key in our slot.
    return {};
  }
}

function write(all) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Not being able to remember is not a reason to refuse to play.
  }
}

/*
 * Each round is kept whole, not just the total: enough to redraw the results
 * screen — its map, its distances, its share text — without the sky data, which
 * is large and can be regenerated from the day's seed anyway.
 */
export function recordPlay({ date = new Date(), tier, total, rounds }) {
  const key = String(dayKey(date));
  const all = read();

  all[key] = {
    ...(all[key] || {}),
    [tier]: {
      total,
      at: new Date().toISOString(),
      submitted: false,
      rounds: rounds.map((entry) => ({
        lat: entry.round.lat,
        lon: entry.round.lon,
        date: entry.round.date.toISOString(),
        guessLat: entry.guess.lat,
        guessLon: entry.guess.lon,
        distanceKm: entry.result.distanceKm,
        roundScore: entry.result.roundScore
      }))
    }
  };

  // Oldest first, so trimming from the front drops the stalest days.
  const keys = Object.keys(all).sort();
  for (const stale of keys.slice(0, Math.max(0, keys.length - KEEP_DAYS))) delete all[stale];

  write(all);
  return all[key];
}

// What this browser has played on a given day, as { easy, hard }, either null.
export function playedOn(date = new Date()) {
  const day = read()[String(dayKey(date))] || {};
  return { easy: day.easy || null, hard: day.hard || null };
}

// The moon row, and the share text, want distances rather than whole rounds.
export function distancesOf(record) {
  return (record?.rounds || []).map((round) => Math.round(round.distanceKm));
}

/*
 * Rebuilt into the shape the results screen takes, so a stored game and a game
 * just finished render through exactly the same component.
 */
export function roundsOf(record) {
  return (record?.rounds || []).map((round) => ({
    round: { lat: round.lat, lon: round.lon, date: new Date(round.date) },
    guess: { lat: round.guessLat, lon: round.guessLon },
    result: { distanceKm: round.distanceKm, roundScore: round.roundScore }
  }));
}

/*
 * Noted once a score reaches the board, so a revisited result does not offer to
 * post it a second time — which matters more now that submission can happen
 * without anyone pressing a button. Nothing on the server rejects a duplicate:
 * every POST inserts a row, so a second submission is a second entry on the
 * board rather than an error, and this flag is the only thing preventing it.
 *
 * The name goes in beside it so a result opened tomorrow can say which name it
 * went up under, rather than an unattributed "saved".
 */
export function markSubmitted(date = new Date(), tier, name = null) {
  const key = String(dayKey(date));
  const all = read();
  if (!all[key] || !all[key][tier]) return;
  all[key][tier].submitted = true;
  if (name) all[key][tier].name = name;

  /*
   * Also noted a level up, on the day rather than on the game.
   *
   * The per-game flag is reset by `recordPlay`, and rightly so — replay the
   * daily and that genuinely is a new, unsubmitted score. But it means the flag
   * cannot answer "has this browser already put something on today's board",
   * which is the question automatic submission has to ask before it posts. This
   * one survives a replay because `recordPlay` spreads the existing day object
   * rather than replacing it.
   */
  all[key].posted = { ...(all[key].posted || {}), [tier]: name || true };
  write(all);
}

// Which tiers this browser has already put a score on today's board for.
export function postedOn(date = new Date()) {
  return read()[String(dayKey(date))]?.posted || {};
}
